import type { Client, Task } from '../types/crm';
import { idsMatch } from '../utils/crmHelpers';
import {
  currentMonthKeySingapore,
  taskDueMonthKey as taskMonthBucket,
} from '../utils/taskHelpers';

export type AccountVisitTaskRow = {
  taskId: string;
  title: string;
  status: string;
  dueDate: string;
  ownerId: string;
  ownerLabel: string;
};

export type AccountMonthVisitSummary = {
  clientId: string;
  companyName: string;
  code: string;
  accountSalesperson: string;
  monthKey: string;
  visitCount: number;
  salespeople: string[];
  titles: string[];
  tasks: AccountVisitTaskRow[];
};

export type AccountVisitHistory = {
  clientId: string;
  companyName: string;
  code: string;
  accountSalesperson: string;
  months: AccountMonthVisitSummary[];
  totalVisits: number;
};

export type AnalyticsTaskDiagnostics = {
  scopedTasks: number;
  afterCancelled: number;
  withMonthKey: number;
  inSelectedMonth: number;
  linkedInSelectedMonth: number;
  unlinkedInSelectedMonth: number;
};

export type AccountCoverageSummary = {
  clientId: string;
  companyName: string;
  code: string;
  accountSalesperson: string;
  met: boolean;
  visitCount: number;
  lastTaskMonthKey: string | null;
  salespeople: string[];
  titles: string[];
};

const UNLINKED_PREFIX = '__unlinked__:';

function isCancelledTask(task: Task): boolean {
  return String(task.status ?? '')
    .trim()
    .toLowerCase() === 'cancelled';
}

function safeText(value: unknown, fallback = '—'): string {
  if (value == null) return fallback;
  const text = String(value).trim();
  return text || fallback;
}

/** Supabase may return bigint ids as numbers — never call .trim() on them directly. */
function linkedClientId(task: Pick<Task, 'client_id'>): string | null {
  if (task.client_id == null || task.client_id === '') return null;
  const id = String(task.client_id).trim();
  return id || null;
}

function accountSalespersonLabel(ownerId?: string | null): string {
  return safeText(ownerId, '—');
}

/** Align with Dashboard pie: Singapore month of due_date, else created_at. */
function analyticsTaskMonthKey(task: Task): string | null {
  return taskMonthBucket(task);
}

export function currentAnalyticsMonthKey(): string {
  try {
    return currentMonthKeySingapore();
  } catch {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  }
}

export function isUnlinkedAnalyticsClientId(clientId: string): boolean {
  return clientId.startsWith(UNLINKED_PREFIX);
}

function unlinkedBucket(task: Task): { key: string; client: Client } {
  const prospect = safeText(task.prospect_company_name, '');
  const label =
    prospect && prospect !== '—' ? prospect : 'No account linked';
  const key = `${UNLINKED_PREFIX}${label}`;
  return {
    key,
    client: {
      id: key,
      company_name: label,
      code: '—',
    } as Client,
  };
}

function bucketForTask(
  task: Task,
  clients: Client[],
): { key: string; client: Client } | null {
  const clientId = linkedClientId(task);
  if (clientId) {
    const client =
      findClient(clients, clientId) ??
      ({
        id: clientId,
        company_name: `Unknown account (${clientId})`,
        code: '—',
      } as Client);
    return { key: String(client.id), client };
  }
  // Still show visit/prospect tasks that have no CRM account yet.
  return unlinkedBucket(task);
}

function taskMatchesHistoryClient(task: Task, client: Client): boolean {
  if (isUnlinkedAnalyticsClientId(String(client.id))) {
    const bucket = unlinkedBucket(task);
    return bucket.key === String(client.id);
  }
  return idsMatch(task.client_id, client.id);
}

/** Newest month key with any non-cancelled analytics-eligible task. */
export function latestMonthKeyWithAccountTasks(tasks: Task[]): string | null {
  let latest: string | null = null;
  for (const task of tasks) {
    if (isCancelledTask(task)) continue;
    const key = analyticsTaskMonthKey(task);
    if (!key) continue;
    if (!latest || key > latest) latest = key;
  }
  return latest;
}

export function countAccountTasksInMonth(tasks: Task[], monthKey: string): number {
  let count = 0;
  for (const task of tasks) {
    if (isCancelledTask(task)) continue;
    if (analyticsTaskMonthKey(task) === monthKey) count += 1;
  }
  return count;
}

export function buildAnalyticsTaskDiagnostics(
  tasks: Task[],
  monthKey: string,
): AnalyticsTaskDiagnostics {
  let afterCancelled = 0;
  let withMonthKey = 0;
  let inSelectedMonth = 0;
  let linkedInSelectedMonth = 0;
  let unlinkedInSelectedMonth = 0;

  for (const task of tasks) {
    if (isCancelledTask(task)) continue;
    afterCancelled += 1;
    const key = analyticsTaskMonthKey(task);
    if (!key) continue;
    withMonthKey += 1;
    if (key !== monthKey) continue;
    inSelectedMonth += 1;
    if (linkedClientId(task)) linkedInSelectedMonth += 1;
    else unlinkedInSelectedMonth += 1;
  }

  return {
    scopedTasks: tasks.length,
    afterCancelled,
    withMonthKey,
    inSelectedMonth,
    linkedInSelectedMonth,
    unlinkedInSelectedMonth,
  };
}

export function shiftMonthKey(monthKey: string, deltaMonths: number): string {
  const match = /^(\d{4})-(\d{2})$/.exec(monthKey.trim());
  if (!match) return currentAnalyticsMonthKey();
  const y = Number(match[1]);
  const m = Number(match[2]);
  const date = new Date(Date.UTC(y, m - 1 + deltaMonths, 1));
  const year = date.getUTCFullYear();
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  return `${year}-${month}`;
}

export function formatMonthKeyLabel(monthKey: string): string {
  const match = /^(\d{4})-(\d{2})$/.exec(monthKey.trim());
  if (!match) return monthKey;
  const date = new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, 1));
  return new Intl.DateTimeFormat('en-GB', {
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(date);
}

/** Inclusive rolling window ending at `endMonthKey` (oldest → newest). */
export function rollingMonthKeys(endMonthKey: string, count = 3): string[] {
  const n = Math.max(1, Math.floor(count));
  const keys: string[] = [];
  for (let i = n - 1; i >= 0; i -= 1) {
    keys.push(shiftMonthKey(endMonthKey, -i));
  }
  return keys;
}

export function formatMonthRangeLabel(monthKeys: string[]): string {
  if (monthKeys.length === 0) return '—';
  if (monthKeys.length === 1) return formatMonthKeyLabel(monthKeys[0]!);
  const first = formatMonthKeyLabel(monthKeys[0]!);
  const last = formatMonthKeyLabel(monthKeys[monthKeys.length - 1]!);
  return `${first} – ${last}`;
}

/**
 * Coverage over scoped CRM accounts for a set of month keys.
 * Unlinked (no client_id) tasks appear as extra "met" rows; they never create "not met" rows.
 */
export function buildAccountCoverageSummaries(
  scopedClients: Client[],
  allClientsForLookup: Client[],
  tasks: Task[],
  monthKeys: string[],
  ownerLabel: (ownerId?: string) => string,
): AccountCoverageSummary[] {
  const monthSet = new Set(monthKeys);
  const tasksByClient = new Map<string, Task[]>();
  const unlinkedByKey = new Map<string, { client: Client; tasks: Task[] }>();
  const lookup = allClientsForLookup.length > 0 ? allClientsForLookup : scopedClients;

  for (const task of tasks) {
    if (isCancelledTask(task)) continue;
    const monthKey = analyticsTaskMonthKey(task);
    if (!monthKey || !monthSet.has(monthKey)) continue;

    const clientId = linkedClientId(task);
    if (clientId) {
      const canonical = findClient(lookup, clientId);
      const mapKey = canonical ? String(canonical.id) : clientId;
      const list = tasksByClient.get(mapKey) ?? [];
      list.push(task);
      tasksByClient.set(mapKey, list);
      continue;
    }

    const bucket = unlinkedBucket(task);
    const existing = unlinkedByKey.get(bucket.key);
    if (existing) existing.tasks.push(task);
    else unlinkedByKey.set(bucket.key, { client: bucket.client, tasks: [task] });
  }

  const summarizeTasks = (
    client: Client,
    monthTasks: Task[],
    metForced?: boolean,
  ): AccountCoverageSummary => {
    const visitRows = monthTasks.map((task) => toVisitRow(task, ownerLabel));
    let lastTaskMonthKey: string | null = null;
    for (const task of monthTasks) {
      const key = analyticsTaskMonthKey(task);
      if (!key) continue;
      if (!lastTaskMonthKey || key > lastTaskMonthKey) lastTaskMonthKey = key;
    }
    const salespeople = [...new Set(visitRows.map((row) => row.ownerLabel).filter(Boolean))].sort(
      (a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }),
    );
    const titles = [...new Set(visitRows.map((row) => row.title))].sort((a, b) =>
      a.localeCompare(b, undefined, { sensitivity: 'base' }),
    );
    const met = metForced ?? monthTasks.length > 0;
    return {
      clientId: String(client.id),
      companyName: safeText(client.company_name, 'Unnamed account'),
      code: safeText(client.code, '—'),
      accountSalesperson: accountSalespersonLabel(client.owner_id),
      met,
      visitCount: monthTasks.length,
      lastTaskMonthKey,
      salespeople,
      titles,
    };
  };

  const rows: AccountCoverageSummary[] = [];

  for (const client of scopedClients) {
    const id = String(client.id);
    const monthTasks =
      tasksByClient.get(id) ??
      [...tasksByClient.entries()].find(([key]) => idsMatch(key, client.id))?.[1] ??
      [];
    rows.push(summarizeTasks(client, monthTasks));
  }

  for (const { client, tasks: monthTasks } of unlinkedByKey.values()) {
    rows.push(summarizeTasks(client, monthTasks, true));
  }

  return rows.sort((a, b) => {
    if (a.met !== b.met) return a.met ? 1 : -1; // not met first
    if (b.visitCount !== a.visitCount) return b.visitCount - a.visitCount;
    return a.companyName.localeCompare(b.companyName, undefined, { sensitivity: 'base' });
  });
}

function toVisitRow(
  task: Task,
  ownerLabel: (ownerId?: string) => string,
): AccountVisitTaskRow {
  const ownerRef = task.owner_id == null ? '' : String(task.owner_id);
  let label = '—';
  try {
    label = safeText(ownerLabel(ownerRef || undefined), safeText(ownerRef));
  } catch {
    label = safeText(ownerRef);
  }
  return {
    taskId: String(task.id),
    title: safeText(task.title, 'Untitled task'),
    status: safeText(task.status),
    dueDate: String(task.due_date ?? task.created_at ?? ''),
    ownerId: safeText(ownerRef, ''),
    ownerLabel: label,
  };
}

function summarizeMonthTasks(
  client: Client,
  monthKey: string,
  monthTasks: Task[],
  ownerLabel: (ownerId?: string) => string,
): AccountMonthVisitSummary {
  const rows = monthTasks
    .map((task) => toVisitRow(task, ownerLabel))
    .sort((a, b) => {
      const dueCmp = a.dueDate.localeCompare(b.dueDate);
      if (dueCmp !== 0) return dueCmp;
      return a.title.localeCompare(b.title);
    });

  const salespeople = [...new Set(rows.map((row) => row.ownerLabel).filter(Boolean))].sort((a, b) =>
    a.localeCompare(b, undefined, { sensitivity: 'base' }),
  );
  const titles = [...new Set(rows.map((row) => row.title))].sort((a, b) =>
    a.localeCompare(b, undefined, { sensitivity: 'base' }),
  );

  return {
    clientId: String(client.id),
    companyName: safeText(client.company_name, 'Unnamed account'),
    code: safeText(client.code, '—'),
    accountSalesperson: accountSalespersonLabel(client.owner_id),
    monthKey,
    visitCount: rows.length,
    salespeople,
    titles,
    tasks: rows,
  };
}

function findClient(clients: Client[], clientId: string): Client | undefined {
  return clients.find((client) => idsMatch(client.id, clientId));
}

export function resolveAnalyticsClient(
  clients: Client[],
  clientId: string,
): Client | null {
  if (!clientId) return null;
  if (isUnlinkedAnalyticsClientId(clientId)) {
    return {
      id: clientId,
      company_name: clientId.slice(UNLINKED_PREFIX.length) || 'No account linked',
      code: '—',
    } as Client;
  }
  return findClient(clients, clientId) ?? null;
}

/** Per-account visit summaries for one calendar month (due_date, else created_at). */
export function buildAccountMonthVisitSummaries(
  clients: Client[],
  tasks: Task[],
  monthKey: string,
  ownerLabel: (ownerId?: string) => string,
): AccountMonthVisitSummary[] {
  const byClient = new Map<string, { client: Client; tasks: Task[] }>();

  for (const task of tasks) {
    if (isCancelledTask(task)) continue;
    if (analyticsTaskMonthKey(task) !== monthKey) continue;
    const bucket = bucketForTask(task, clients);
    if (!bucket) continue;
    const existing = byClient.get(bucket.key);
    if (existing) {
      existing.tasks.push(task);
    } else {
      byClient.set(bucket.key, { client: bucket.client, tasks: [task] });
    }
  }

  const rows: AccountMonthVisitSummary[] = [];
  for (const { client, tasks: monthTasks } of byClient.values()) {
    rows.push(summarizeMonthTasks(client, monthKey, monthTasks, ownerLabel));
  }

  return rows.sort((a, b) => {
    if (b.visitCount !== a.visitCount) return b.visitCount - a.visitCount;
    return a.companyName.localeCompare(b.companyName, undefined, { sensitivity: 'base' });
  });
}

/** Multi-month history for one account (newest month first). */
export function buildAccountVisitHistory(
  client: Client,
  tasks: Task[],
  ownerLabel: (ownerId?: string) => string,
): AccountVisitHistory {
  const byMonth = new Map<string, Task[]>();

  for (const task of tasks) {
    if (isCancelledTask(task)) continue;
    if (!taskMatchesHistoryClient(task, client)) continue;
    const key = analyticsTaskMonthKey(task);
    if (!key) continue;
    const list = byMonth.get(key) ?? [];
    list.push(task);
    byMonth.set(key, list);
  }

  const months = [...byMonth.entries()]
    .sort((a, b) => b[0].localeCompare(a[0]))
    .map(([monthKey, monthTasks]) => summarizeMonthTasks(client, monthKey, monthTasks, ownerLabel));

  return {
    clientId: String(client.id),
    companyName: safeText(client.company_name, 'Unnamed account'),
    code: safeText(client.code, '—'),
    accountSalesperson: accountSalespersonLabel(client.owner_id),
    months,
    totalVisits: months.reduce((sum, row) => sum + row.visitCount, 0),
  };
}
